<template>
  <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col" :class="{ 'shrink-0': collapsed }">
    <!-- Header（可點擊收合；對齊 UserListPanel 語彙）-->
    <button
      type="button"
      class="px-4 pt-3 pb-2 w-full text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 bg-gradient-to-b from-slate-50/50 to-transparent"
      :class="collapsed ? '' : 'border-b border-slate-100'"
      :aria-expanded="!collapsed"
      :aria-label="collapsed ? $t('permission_expand_aria') : $t('permission_collapse_aria')"
      @click="collapsed = !collapsed"
    >
      <div class="flex items-center gap-2">
        <v-icon size="16" :color="tab === 'department' ? '#0d9488' : '#4f46e5'">
          {{ tab === 'department' ? 'mdi-domain' : 'mdi-account-group' }}
        </v-icon>
        <span class="text-sm font-bold text-slate-700">
          {{ tab === 'department' ? $t('permission_dept_short') : $t('permission_group_short') }}
        </span>
        <div
          class="ml-auto inline-flex items-center justify-center min-w-[26px] h-[22px] px-2 rounded-full text-white text-[11px] leading-none font-bold"
          :class="tab === 'department' ? 'bg-teal-500' : 'bg-indigo-500'"
        >
          {{ list.length }}
        </div>
        <v-icon size="16" color="#94a3b8" aria-hidden="true">
          {{ collapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
        </v-icon>
      </div>
    </button>

    <!-- Tabs（收合時不顯示；active 色吃 variant：dept=teal、group=indigo）-->
    <div v-show="!collapsed" class="flex border-b border-slate-200 shrink-0">
      <button
        type="button"
        class="flex-1 py-2.5 text-xs font-semibold transition-colors duration-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-teal-200"
        :class="tab === 'department'
          ? 'text-teal-600 border-b-2 border-teal-500 bg-teal-50/40'
          : 'text-slate-500 hover:text-slate-700 border-b-2 border-transparent'"
        @click="changeTab('department')"
      >
        {{ $t('permission_dept_short') }}
      </button>
      <button
        type="button"
        class="flex-1 py-2.5 text-xs font-semibold transition-colors duration-200 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-200"
        :class="tab === 'group'
          ? 'text-indigo-600 border-b-2 border-indigo-500 bg-indigo-50/40'
          : 'text-slate-500 hover:text-slate-700 border-b-2 border-transparent'"
        @click="changeTab('group')"
      >
        {{ $t('permission_group_short') }}
      </button>
    </div>

    <!-- Add input -->
    <div v-show="!collapsed" class="px-3 pt-3 pb-2 shrink-0">
      <div class="flex gap-1.5">
        <input
          v-model="newName"
          :placeholder="tab === 'department' ? $t('permission_new_dept_ph') : $t('permission_new_group_ph')"
          class="flex-1 text-xs px-3 py-1.5 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-400 placeholder-slate-400 transition-colors duration-200"
          @keyup.enter="handleAdd"
        />
        <button
          type="button"
          class="w-7 h-7 rounded-lg flex items-center justify-center transition-colors duration-200 cursor-pointer shrink-0"
          :class="newName.trim()
            ? 'bg-indigo-500 text-white hover:bg-indigo-600'
            : 'bg-slate-100 text-slate-400 cursor-not-allowed'"
          :disabled="!newName.trim()"
          :aria-label="$t('permission_add_aria')"
          @click="handleAdd"
        >
          <v-icon size="16" :color="newName.trim() ? 'white' : '#94a3b8'">mdi-plus</v-icon>
        </button>
      </div>
    </div>

    <!-- List -->
    <ul v-show="!collapsed" class="overflow-y-auto flex-1 py-1 role-list-scroll min-h-0">
      <li
        v-for="item in list"
        :key="item.id"
        class="group flex items-center gap-2.5 px-3 py-1.5 cursor-pointer border-l-2 transition-colors duration-200"
        :class="item.id === selectedId
          ? (tab === 'department' ? 'bg-teal-50/80 border-teal-500' : 'bg-indigo-50/80 border-indigo-500')
          : 'border-transparent hover:bg-slate-50'"
        @click="$emit('select', item.id)"
      >
        <div
          class="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0"
          :class="avatarClass(item.id)"
        >
          {{ item.name.slice(0, 1) }}
        </div>
        <div class="flex-1 min-w-0">
          <div class="text-[13px] font-medium text-slate-800 truncate">{{ item.name }}</div>
          <div class="text-[10px] text-slate-400">
            {{ memberCountOf(item) }} {{ $t('permission_member_count_label') }}
          </div>
        </div>
        <button
          type="button"
          class="opacity-0 group-hover:opacity-100 w-5 h-5 rounded text-rose-400 hover:bg-rose-50 hover:text-rose-600 flex items-center justify-center transition-colors duration-200 cursor-pointer shrink-0 focus:outline-none focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-rose-300"
          :aria-label="$t('permission_delete_aria')"
          @click.stop="$emit('delete', item)"
        >
          <v-icon size="12" color="currentColor">mdi-close</v-icon>
        </button>
      </li>
      <li
        v-if="list.length === 0"
        class="flex flex-col items-center py-10 text-slate-400"
      >
        <v-icon size="40" color="#cbd5e1">mdi-shield-outline</v-icon>
        <div class="mt-2 text-xs">{{ $t('permission_empty_list') }}</div>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'RoleListPanel',
  props: {
    tab: { type: String, default: 'department' },
    list: { type: Array, default: () => [] },
    selectedId: { type: [String, Number], default: null }
  },
  data () {
    return { newName: '', collapsed: false }
  },
  methods: {
    changeTab (next) {
      if (next === this.tab) return
      this.newName = ''
      this.$emit('change-tab', next)
    },
    avatarClass (id) {
      const selected = id === this.selectedId
      if (this.tab === 'department') {
        return selected ? 'bg-teal-500 text-white' : 'bg-teal-100 text-teal-700'
      }
      return selected ? 'bg-indigo-500 text-white' : 'bg-indigo-100 text-indigo-700'
    },
    memberCountOf (item) {
      if (!item) return 0
      return typeof item.userCount === 'number' ? item.userCount : 0
    },
    handleAdd () {
      const name = this.newName.trim()
      if (!name) return
      this.$emit('add', name)
      this.newName = ''
    }
  }
}
</script>

<style scoped>
.role-list-scroll::-webkit-scrollbar {
  width: 5px;
}
.role-list-scroll::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 999px;
}
.role-list-scroll::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
